import React from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from "recharts";
import { formatINR } from "@/lib/api";
import { BarChart3 } from "lucide-react";

const FUNNEL_COLOR = { Submitted: "#a8a29e", Verification: "#f59e0b", Approved: "#3b82f6", Paid: "#047857", Rejected: "#ef4444" };
const MIX_COLORS = ["#065f46", "#0d9488", "#f59e0b", "#6366f1", "#e11d48", "#78716c", "#0ea5e9", "#a855f7"];

export const DeepAnalytics = ({ data, shareStats }) => {
  if (!data) return null;
  const maxMix = Math.max(1, ...(data.category_mix || []).map((c) => c.count));
  const maxTeam = Math.max(1, ...(data.team_top || []).map((t) => t.earned));
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6" data-testid="deep-analytics">
      <div className="flex items-center gap-2 mb-5">
        <BarChart3 className="w-4 h-4 text-emerald-900" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Deep analytics</span>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Earnings trend */}
        <div data-testid="analytics-earnings">
          <div className="text-sm font-black mb-2">Earnings — last 6 months</div>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.earnings_trend} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="earnGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#047857" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#047857" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                <Tooltip formatter={(v) => [formatINR(v), "Earned"]} contentStyle={{ borderRadius: 12, border: "1px solid #e7e5e4", fontSize: 12 }} />
                <Area type="monotone" dataKey="amount" stroke="#047857" strokeWidth={2.5} fill="url(#earnGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Lead funnel */}
        <div data-testid="analytics-funnel">
          <div className="text-sm font-black mb-2">Lead funnel</div>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.funnel} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                <XAxis dataKey="stage" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e7e5e4", fontSize: 12 }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {data.funnel.map((f) => <Cell key={f.stage} fill={FUNNEL_COLOR[f.stage] || "#a8a29e"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category mix */}
        <div data-testid="analytics-mix">
          <div className="text-sm font-black mb-3">Business mix by category</div>
          {(data.category_mix || []).length === 0 ? (
            <div className="text-sm text-stone-400">No leads yet — your mix appears here.</div>
          ) : (
            <div className="space-y-2">
              {data.category_mix.map((c, i) => (
                <div key={c.category}>
                  <div className="flex justify-between text-xs font-bold text-stone-600"><span>{c.category}</span><span>{c.count}</span></div>
                  <div className="h-2 bg-stone-100 rounded-full mt-1 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(c.count / maxMix) * 100}%`, background: MIX_COLORS[i % MIX_COLORS.length] }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Team performance */}
        <div data-testid="analytics-team">
          <div className="text-sm font-black mb-3">Top team earners {data.team_size > 0 && <span className="text-xs font-bold text-stone-400">· {data.team_size} direct</span>}</div>
          {(data.team_top || []).length === 0 ? (
            <div className="text-sm text-stone-400">Recruit your first partner to see team analytics.</div>
          ) : (
            <div className="space-y-2">
              {data.team_top.map((t, i) => (
                <div key={t.name} className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-full bg-emerald-900 text-amber-400 grid place-items-center font-bold text-[10px] shrink-0">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between text-xs font-bold text-stone-600"><span className="truncate">{t.name}</span><span>{formatINR(t.earned)}</span></div>
                    <div className="h-2 bg-stone-100 rounded-full mt-1 overflow-hidden">
                      <div className="h-full rounded-full bg-amber-500" style={{ width: `${(t.earned / maxTeam) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {shareStats && shareStats.total_shares > 0 && (
        <div className="mt-6 pt-5 border-t border-stone-100 flex flex-wrap items-center gap-3 text-xs" data-testid="analytics-shares">
          <span className="font-black text-stone-600">{shareStats.total_shares} product shares</span>
          <span className="text-stone-400">→ {shareStats.leads_from_shared_products} leads</span>
          {(shareStats.top_products || []).slice(0, 3).map((tp) => (
            <span key={tp.product_id} className="px-2.5 py-1 bg-stone-100 rounded-full font-semibold text-stone-600">{tp.product_name} · {tp.count}</span>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeepAnalytics;
